import React, { SyntheticEvent } from 'react';
import cn from 'classnames';
import Note from '@components/Note';
import ContactAvatar from '@components/ContactAvatar';
import NoteModel from '@models/Note';
import styles from './NoteItem.module.scss';
import { selectContact } from '@actions/contacts';
import { useSelector } from 'react-redux';
import {
  PenIcon,
  ExpandNoteIcon,
  LockIcon,
  ReminderAlarmClockIcon,
  XIcon,
} from '@components/Icons';
import ContactLink from '@components/ContactLink';
import { ActionIcon } from '@views/Activity/ActivityListItem/ItemActions/ItemActions';
import { NoteIcon } from '@components/Note/IconAndColorPicker';
import Tooltip, { Overlay } from '@components/Tooltip';
import { useTheme } from '@support/hooks/use-theme';

export default function NoteItem({
  note,
  active = false,
  onSelect,
  onDelete,
  ...props
}: {
  note: NoteModel;
  active: boolean;
  onSelect: (note: NoteModel, focusMode?: boolean) => void;
  onDelete: (note: NoteModel, e: SyntheticEvent) => void;
}) {
  const contact = useSelector(selectContact(note.contact?.id, note.contact));
  const [theme] = useTheme();

  let noteIcon = null;

  if (note.type.icon) {
    noteIcon = (
      <NoteIcon
        selectedColor={note.type.color}
        selectedIconType={note.type.icon}
        editable={false}
      />
    );
  }

  if (note.reminder?.date) {
    const isPast = note.reminder.date.isPast;
    noteIcon = (
      <ReminderAlarmClockIcon
        style={{ opacity: isPast ? 0.4 : 1, minWidth: 16 }}
        width={16}
        height={16}
        colorized={!isPast}
      />
    );
  }

  return (
    <>
      <ContactAvatar size="sm" contact={contact} />
      <div className={styles.contactDetails}>
        <ContactLink contact={contact} className={styles.contactLink}>
          {contact.displayName}
        </ContactLink>
      </div>
      <div className={cn(styles.note, active && styles.highlighted)}>
        {noteIcon}
        {note.type.label && <div className={styles.noteLabel}>{note.type.label}</div>}
        <Note note={note} inline readOnly omitActions selected={false} {...props} />
      </div>
      <div className={cn('text-small text-secondary', styles.date, active && styles.hidden)}>
        {note.updated.format('MMM d yyyy')}
      </div>
      {active && !note.isReadOnly && (
        <div className={styles.actions}>
          <ActionIcon
            handler={(e) => {
              e.preventDefault();
              onSelect(note, false);
            }}
            icon={<PenIcon width={20} height={20} hoverOpacity={false} />}
            shortcut="Enter"
            label="Edit"
          />
          <ActionIcon
            handler={(e) => {
              e.preventDefault();
              onSelect(note, true);
            }}
            icon={<ExpandNoteIcon />}
            shortcut="f"
            label="Focus Mode"
          />
          <ActionIcon
            handler={(e) => onDelete(note, e)}
            icon={<XIcon />}
            shortcut="e"
            label="Delete"
          />
        </div>
      )}
      {active && note.isReadOnly && (
        <div className={styles.actions}>
          <Tooltip placement="bottom" overlay={<Overlay>Read-only</Overlay>}>
            <LockIcon
              width="16"
              height="18"
              viewBox="7.5 7 9 10"
              customColor={theme === 'light' ? '#bbb' : '#999'}
            />
          </Tooltip>
        </div>
      )}
    </>
  );
}
